import React, { useState, useEffect } from "react";

export interface BracketProps {
	color?: string; // Hex color or CSS color value
	side: "left" | "right";
	height?: number | string; // Can be a number (pixels) or CSS value like "clamp(20px, 2.5vw, 36px)"
	className?: string;
	animateColors?: boolean; // Toggle to animate through colors
}

const colorMap: Record<string, string> = {
	blue: "#6C84FA",
	green: "#73BFB8",
	red: "#E9AFA3",
	yellow: "#FFB115",
};

const colorArray = Object.values(colorMap);

const HandDrawnBracket: React.FC<BracketProps> = ({
	color,
	side,
	height = 32,
	className,
	animateColors = false,
}) => {
	// Default to first color in array if no color provided
	const defaultColor = colorMap[color || "blue"];
	const [currentColor, setCurrentColor] = useState<string>(defaultColor);
	const [colorIndex, setColorIndex] = useState<number>(0);

	useEffect(() => {
		if (animateColors) {
			// Find the initial color index if the provided color matches one in the map
			const initialColor = colorMap[color || "blue"];
			const initialIndex = colorArray.findIndex(
				(c) => c.toLowerCase() === initialColor.toLowerCase(),
			);
			const startIndex = initialIndex !== -1 ? initialIndex : 0;

			setColorIndex(startIndex);
			setCurrentColor(colorArray[startIndex]);

			const interval = setInterval(() => {
				setColorIndex((prevIndex) => {
					const nextIndex = (prevIndex + 1) % colorArray.length;
					setCurrentColor(colorArray[nextIndex]);
					return nextIndex;
				});
			}, 1000); // Switch color every second

			return () => clearInterval(interval);
		} else {
			// Reset to original color when animation is disabled, or default if no color provided
			setCurrentColor(colorMap[color || "blue"]);
		}
	}, [animateColors, color]);
	// Original SVG path from Figma (left bracket)
	const leftBracketPath = `M4.25227 0.260536C1.38225 0.71652 1.03361 0.93102 0.819031 2.32579C-0.0929367 8.95097 -0.0124689 19.841 0.0143537 37.973C0.0143537 38.8582 -0.119786 39.636 1.6505 39.8774C4.30594 40.2261 10.8775 39.7969 11.7894 39.0191C13.1573 37.8657 11.6017 37.3024 7.73921 37.5438L4.7351 37.7316C4.44005 14.235 5.03015 11.6601 5.27155 2.35264L6.50542 2.19168C9.5632 1.81617 10.8775 1.14563 9.2413 0.475063C8.14157 -0.0077439 7.22958 -0.19545 4.25227 0.260536Z`;

	// Original SVG dimensions
	const originalWidth = 13;
	const originalHeight = 40;
	const aspectRatio = originalWidth / originalHeight; // 0.325

	// Handle height: if number, calculate width; if string (clamp), use aspect-ratio
	const isHeightNumeric = typeof height === "number";

	return (
		<svg
			{...(isHeightNumeric && {
				width: height * aspectRatio,
				height: height,
			})}
			viewBox={`0 0 ${originalWidth} ${originalHeight}`}
			className={`pointer-events-none ${className || ""}`.trim()}
			style={{
				[side]: side === "left" ? "0" : "-6px",
				top: "0",
				transform: side === "right" ? "scaleX(-1)" : "none",
				transformOrigin: "center",
				// Use aspect-ratio when height is a string (clamp) to maintain proportions
				...(typeof height === "string" && {
					aspectRatio: `${originalWidth} / ${originalHeight}`,
					width: "auto",
					height: height,
				}),
			}}
		>
			<path
				d={leftBracketPath}
				fill={currentColor}
				fillRule="evenodd"
				clipRule="evenodd"
			/>
		</svg>
	);
};

export default HandDrawnBracket;
